/**
 * @file Submission UI — contributorDetailsReadonly.
 * Contributor Details — read-only data + field order for Dataset Overview.
 */

import type { FormField } from "../../interfaces/formInterfaces";
import {
  dedupeFormFieldsByNameSorted,
  getStageOrderedFields,
  mergeStageFormFields,
  sortFieldsByOrder,
  type StageFieldsSource,
} from "./backgroundStageFieldOrder";

/** Type definition — Contributor Any Obj. */
export type ContributorAnyObj = Record<string, unknown>;

/** Type definition — Contributor Field Schema. */
export type ContributorFieldSchema = {
  name?: string;
  label?: string;
  order?: number;
  type?: string;
  dropdown?: {
    options?: Array<{ label?: string; value?: string | number; id?: number }>;
  } | null;
};

const CONTRIBUTOR_FORM_ALIASES: Record<string, string[]> = {
  name: ["name", "contributorName"],
  email_address: ["email_address", "emailAddress", "email", "contributorEmail"],
  Contributor_ID: ["Contributor_ID", "contributorId", "userCode", "user_code"],
  background_other: ["background_other", "backgroundOther", "backgroundIfOther"],
};

function valueForField(obj: ContributorAnyObj | null | undefined, fieldName: string) {
  if (!obj) return undefined;
  if (Object.prototype.hasOwnProperty.call(obj, fieldName)) return obj[fieldName];
  const camel = fieldName.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
  if (Object.prototype.hasOwnProperty.call(obj, camel)) return obj[camel];
  return undefined;
}

/** Exported function — read Contributor Value For Form Field. */
export function readContributorValueForFormField(
  obj: ContributorAnyObj | null | undefined,
  fieldName: string
): unknown {
  if (!obj) return undefined;
  const aliases = CONTRIBUTOR_FORM_ALIASES[fieldName];
  if (aliases) {
    for (const key of aliases) {
      const v = obj[key];
      if (v !== undefined && v !== null && v !== "") return v;
    }
  }
  return valueForField(obj, fieldName);
}

/** Exported function — build Contributor Details Readonly Object. */
export function buildContributorDetailsReadonlyObject(
  submission: ContributorAnyObj
): ContributorAnyObj | null {
  const profile =
    submission.contributorProfile ?? submission.contributor_profile ?? null;
  const user =
    submission.contributor ??
    submission.user ??
    submission.contributorUser ??
    null;

  if (!profile && !user) return null;

  const p = (profile && typeof profile === "object" ? profile : {}) as ContributorAnyObj;
  const u = (user && typeof user === "object" ? user : {}) as ContributorAnyObj;

  const backgroundOther =
    p.backgroundOther ??
    p.background_other ??
    p.backgroundIfOther ??
    "";

  return {
    ...p,
    name: u.name ?? p.name ?? submission.contributorName ?? "",
    email: u.email ?? p.email ?? submission.contributorEmail ?? "",
    email_address:
      u.email ??
      p.email_address ??
      p.emailAddress ??
      p.email ??
      submission.contributorEmail ??
      "",
    Contributor_ID:
      u.userCode ?? u.user_code ?? p.Contributor_ID ?? p.contributorId ?? "",
    background_other: backgroundOther,
    backgroundOther,
  };
}

/** Exported function — contributor Details Display Field Names. */
export function contributorDetailsDisplayFieldNames(
  formSchema: ContributorAnyObj[]
): ContributorFieldSchema[] {
  const stage = formSchema.find((s) => s.code === "CONTRIBUTOR_DETAILS");
  const schema = stage
    ? getStageOrderedFields(stage as StageFieldsSource)
    : [];
  const ensure: ContributorFieldSchema[] = [
    { name: "name", label: "Name", order: -30 },
    { name: "email_address", label: "Email address", order: -20 },
    { name: "background_other", label: "Background (If Other)", order: 900 },
  ];
  const names = new Set(schema.map((f) => f.name).filter(Boolean));
  const missing = ensure.filter((f) => f.name && !names.has(f.name));
  return sortFieldsByOrder([
    ...schema.map((f) => ({
      name: f.name,
      label: f.label,
      order: f.order,
      type: f.type,
      dropdown: f.dropdown,
    })),
    ...missing,
  ]);
}

/** Exported function — resolve Contributor Stage Fields. */
export function resolveContributorStageFields(
  formSchema: ContributorAnyObj[]
): FormField[] {
  const stage = formSchema.find((s) => s.code === "CONTRIBUTOR_DETAILS");
  if (!stage) return [];
  return dedupeFormFieldsByNameSorted(
    mergeStageFormFields(stage as StageFieldsSource)
  );
}

/** Exported function — ordered Contributor Form Fields. */
export function orderedContributorFormFields(
  formSchema: ContributorAnyObj[]
): FormField[] {
  const allByName = new Map(
    resolveContributorStageFields(formSchema).map((f) => [f.name, f])
  );
  const order = contributorDetailsDisplayFieldNames(formSchema);

  return order
    .map((slot) => {
      const full = slot.name ? allByName.get(slot.name) : undefined;
      if (full) return full;
      if (!slot.name) return null;
      const type =
        slot.name === "email_address"
          ? "EMAIL"
          : slot.type === "DROPDOWN"
            ? "DROPDOWN"
            : "TEXT";
      return {
        id: 0,
        name: slot.name,
        label: slot.label ?? slot.name,
        type,
        isMandatory: false,
        isEnabled: true,
        isEditable: false,
        order: slot.order,
        dropdown: slot.dropdown ?? null,
      } as FormField;
    })
    .filter((f): f is FormField => Boolean(f));
}

/** Exported function — display Contributor Field Value. */
export function displayContributorFieldValue(
  field: FormField,
  data: ContributorAnyObj
): string {
  const raw = readContributorValueForFormField(data, field.name);
  if (raw === null || raw === undefined) return "";
  if (field.type === "DROPDOWN") {
    const options = field.dropdown?.options ?? [];
    const match = options.find(
      (o) => String(o.value ?? o.id ?? "") === String(raw)
    );
    if (match?.label) return String(match.label);
  }
  return String(raw);
}
